import React from 'react';
import { Typography, Box, Paper, List, ListItem, ListItemText, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

const DockerInstructions = () => {
  const SectionHeader = styled(Typography)({
    color: '#003366', // USCIS blue
    margin: '24px 0 16px',
    fontWeight: 'bold'
  });

  const BenefitItem = styled(ListItem)({
    paddingLeft: 0,
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
      color: '#003366' // USCIS blue
    }
  });

  return (
    <Box sx={{ p: 4, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h2" component="h1" sx={{ color: '#003366', mb: 4 }}>
        Docker Deployment Guide
      </Typography>
      
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f5f5f5' }}>
        <Typography variant="h3" component="h2" sx={{ color: '#003366', mb: 2 }}>
          Purpose of Docker in E-Unify
        </Typography>
        <Typography paragraph>
          Docker provides containerization for the E-Unify application, ensuring consistent environments across development, testing, and production. Our Docker setup includes:
        </Typography>
        
        <List dense={false}>
          <BenefitItem>
            <ListItemText
              primary="Multi-stage builds"
              secondary="Separate build and runtime environments for optimized production images"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Health checks"
              secondary="Built-in monitoring for application and database availability"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Network isolation"
              secondary="Secure communication between services via dedicated network"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Persistent storage"
              secondary="Data preservation across container restarts"
            />
          </BenefitItem>
        </List>
      </Paper>

      <SectionHeader variant="h3">Dockerfile Breakdown</SectionHeader>
      <Typography paragraph>
        Our Dockerfile uses multi-stage builds for efficiency and security:
      </Typography>
      
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#f5f5f5', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        <Typography variant="h4" sx={{ color: '#B31B1B', mb: 2 }}>Stage 1: Frontend Build</Typography>
        <Typography>
          • Node.js 18 Alpine base image
          • Installs dependencies with npm ci
          • Builds React app with production API URL
        </Typography>
        
        <Typography variant="h4" sx={{ color: '#B31B1B', mt: 3, mb: 2 }}>Stage 2: Backend Setup</Typography>
        <Typography>
          • Node.js 18 Alpine with dumb-init for proper signal handling
          • Installs only production dependencies
          • Copies built frontend from Stage 1
          • Includes server code and seed scripts
        </Typography>
      </Paper>

      <SectionHeader variant="h3">docker-compose.yml Features</SectionHeader>
      <Typography paragraph>
        Our docker-compose configuration provides a complete local development environment:
      </Typography>
      
      <List dense={false}>
        <BenefitItem>
          <ListItemText
            primary="App Service"
            secondary="• Port 3002 exposed\n• Health check endpoint\n• Production environment variables"
          />
        </BenefitItem>
        <Divider component="li" />
        <BenefitItem>
          <ListItemText
            primary="MongoDB Service"
            secondary="• MongoDB 6.0\n• Persistent volume\n• Health check\n• Initialization scripts"
          />
        </BenefitItem>
        <Divider component="li" />
        <BenefitItem>
          <ListItemText
            primary="Network Configuration"
            secondary="• Dedicated bridge network\n• Isolated communication between services"
          />
        </BenefitItem>
      </List>

      <SectionHeader variant="h3">Deployment Benefits</SectionHeader>
      <Typography paragraph>
        Our Docker implementation provides strategic advantages for E-Unify:
      </Typography>
      
      <Paper elevation={3} sx={{ p: 4, bgcolor: '#f5f5f5' }}>
        <List dense={false}>
          <BenefitItem>
            <ListItemText
              primary="Consistency"
              secondary="Identical environments from development through production"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Security"
              secondary="• Minimal Alpine base images\n• Non-root user by default\n• Health monitoring"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Scalability"
              secondary="Ready for Kubernetes orchestration and horizontal scaling"
            />
          </BenefitItem>
          <Divider component="li" />
          <BenefitItem>
            <ListItemText
              primary="Maintainability"
              secondary="Clear separation of concerns between frontend and backend"
            />
          </BenefitItem>
        </List>
      </Paper>
    </Box>
  );
};

export default DockerInstructions;
